/*
 * ===============================================================================================================
 *                                Copyright 2024, Blue Yonder Group, Inc.
 *                                           All Rights Reserved
 *
 *                               THIS IS UNPUBLISHED PROPRIETARY SOURCE CODE OF
 *                                          BLUE YONDER GROUP, INC.
 *
 *
 *                         The copyright notice above does not evidence any actual
 *                                 or intended publication of such source code.
 *
 * ===============================================================================================================
 */
import _ from "lodash";
import { Fragment, useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Autocomplete, Box, Button, Grid, TextField } from "@mui/material";

import { useStyles } from "./styles";
import { LABELS, MESSAGES } from "./constants";
import Loader from "../../shared/loader/Loader";
import { getModules } from "../../store/module/module";
import FormDialog from "../../shared/dialog/FormDialog";
import AutoCompleteComponent from "../../shared/autoCompleteComponent";
import { CustomTooltip } from "../../shared/customTooltip/CustomTooltip";
import { getOrganizations } from "../../store/organization/organizations";
import CustomTransferList from "../customTransferList/CustomTransferList";
import { setSelectedOrganizationAction } from "../../store/organization/organizationActionCreator";
import { updateEntitlements } from "../../store/subEnterprise/subEnterprise";
import { selectAllApplication } from "../../store/application/applicationSelector";
import { selectSelectedSubEnterprise } from "../../store/subEnterprise/subEnterpriseSelector";
import { selectSelectedRealm } from "../../store/realm/realmSelector";
import { getApplications, resetDataOnChangeOrganization } from "../../store/application/application";
import { selectAllOrganizations, selectSelectedOrganization } from "../../store/organization/organizationSelector";
import { updateRealmEntitlements } from "../../store/realm/realm";

interface IEditRealmEntitlementsDialogProps {
  open: boolean;
  refreshOnSave: Function;
  closeHandler: Function;
};

const EditEntitlementDialog = (props: IEditRealmEntitlementsDialogProps) => {
  const classes = useStyles();
  const dispatch = useDispatch();

  const showHideLoader = false;
  const applicationsList = useSelector(selectAllApplication);
  const organizationList = useSelector(selectAllOrganizations);
  const selectedSubEnterprise = useSelector(selectSelectedSubEnterprise);
  const selectedOrganization = useSelector(selectSelectedOrganization);
  const selectedRealm = useSelector(selectSelectedRealm);

  const [apps, setApps] = useState<any[]>([]);
  const [selectedModules, setSelectedModules] = useState<any[]>(selectedRealm?.entitlements || []);
  const [availableModules, setAvailableModules] = useState<any[]>([]);
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    resetDataOnChangeOrganization(dispatch);
    setApps([]);
    setSelectedModules([]);
    setAvailableModules([]);
    setSelectedModules(selectedRealm?.entitlements || []);
    getOrgList('');
  }, [selectedRealm?.entitlements]);

  const getOrgList = async (searchString: string) => {
    const payload = {
      pageNo: 1,
      searchString,
      pageSize: 100,
    }
    getOrganizations(dispatch, payload);
  };

  const getApplicationsForOrganization = async (id: string, searchString: string) => {
    const payload = {
      id,
      pageNo: 1,
      searchString,
      pageSize: 1000,
    };
    getApplications(dispatch, payload);
  };

  const getModuleForApplication = async (id: string) => {
    const payload = {
      pageNo: 1,
      pageSize: 1000,
      searchString: '',
      applicationId: id,
      organizationId: selectedOrganization?.id,
    };
    return await getModules(dispatch, payload);
  };

  const handleOnChangeOrgSearch = (value: string) => {
    if (value.trim().length && value.trim().length > 2) {
      getOrgList(value);
    }
  };

  const handleOnChangeAppSearch = (value: string) => {
    if (value.trim().length && value.trim().length > 2) {
      getApplicationsForOrganization(selectedOrganization?.id, value);
    }
  };

  const handleOrgSelect = (value: any) => {
    setIsLoading(true);
    resetDataOnChangeOrganization(dispatch);
    dispatch(setSelectedOrganizationAction(value));
    if (value) {
      getApplicationsForOrganization(value.id, '');
    }
    setIsLoading(false);
  }

  const handleClose = () => {
    props.closeHandler();
  };

  const handleListOnChange = (itemInView: any, itemAvailable: any) => {
    setSelectedModules(_.uniqBy(itemInView, 'id'));
    setAvailableModules(_.uniqBy(itemAvailable, 'id'));
  };

  const onApplicationChange = async (_event: object, value: any, reason: string, details?: any) => {
    setIsLoading(true);
    setApps(value);
    if (reason === 'selectOption') {
      let newModules: any = [];
      const appModules = await getModuleForApplication(details.option.id);
      newModules = appModules.filter((value: any) => selectedModules.indexOf(value) === -1);
      const allModules = _.uniqBy([...availableModules, ...newModules], 'id');
  
      if (selectedModules.length) {
        const removeExistingEntitlements = _.pullAllBy(allModules, selectedModules, 'id');
        setAvailableModules(removeExistingEntitlements);
      } else {
        setAvailableModules(_.uniqBy([...allModules], 'id'));
      }
    }
    if (reason === 'removeOption') {
      const newFilteredSelectedModules = selectedModules.filter((module: any) => details.option.id !== module.name);
      setSelectedModules(newFilteredSelectedModules);
    }
    if (reason === 'clear') {
      setSelectedModules([]);
      setAvailableModules([]);
    }
    setIsLoading(false);
  };

  const handleSubmit = async () => {
    setIsLoading(true);
    let isSuccess = false;
    let isSuccessRealmEntitlements = false;
    const leftModules = [...availableModules];
    const rightModules = [...selectedModules];
    const payload: any = {
      id: selectedSubEnterprise.id,
      customerId: selectedSubEnterprise.customerId,
      entitlements: [
        {
          op: 'add',
          value: []
        }
      ]
    };
    let removedEntitlements: any = [];
    let addedEntitlements: any = [];
    if (!leftModules.length) {
      addedEntitlements = rightModules;
      removedEntitlements = [];
    } else {
      removedEntitlements = _.pullAllBy(leftModules, selectedModules, 'id');
      addedEntitlements = _.pullAllBy(rightModules, leftModules, 'id');
    }
    if (addedEntitlements.length) {
      payload.entitlements[0].value = addedEntitlements.map((item: any) => item.id);
    }
    isSuccess = await updateEntitlements(dispatch, payload);
    if (isSuccess) {
      isSuccessRealmEntitlements = await updateRealmEntitlements(dispatch, {...payload, id: selectedRealm.id, subEnterpriseId: selectedSubEnterprise.id });
    }
    if (removedEntitlements.length) {
      payload.entitlements[0].value = removedEntitlements.map((item: any) => item.id);
      payload.entitlements[0].op = 'remove';
    }
    //Remove only in the realm level entitlements as sub enterprise entitlements will be used by other realms
    isSuccessRealmEntitlements = await updateRealmEntitlements(dispatch, {...payload, id: selectedRealm.id, subEnterpriseId: selectedSubEnterprise.id });
   
    setIsLoading(false);
    if (isSuccess) {
      props.refreshOnSave();
      handleClose();
    }

  };

  return (
    <Fragment>
      <FormDialog
        dialogLoader={(<Loader showLoader={isLoading} isCenter />)}
        maxWidth={"md"}
        openModal={props.open}
        handleClose={handleClose}
        dialogTitle={LABELS.ENTITLEMENTS}
        dialogAction1={
          <Button
            color="primary"
            name="cancelBtn"
            disableElevation
            variant="outlined"
            onClick={handleClose}
          >
            {LABELS.CANCEL}
          </Button>
        }
        dialogAction2={
          <Button
            color="primary"
            disableElevation
            variant="contained"
            onClick={handleSubmit}
            disabled={showHideLoader}
            id="updateRealmEntitlementBtn"
            name="updateRealmEntitlementBtn"
          >
            {LABELS.SAVE}
          </Button>
        }
      >
        <Grid container>
          <Grid item xs={12} className={classes.formStyle}>
            <Box className={classes.organizationDropdownBox}>
              <Autocomplete
                size="medium"
                id="organization-select"
                options={organizationList}
                aria-controls="options-list"
                ListboxProps={{ id: 'options-list' }}
                className={classes.organizationDropdown}
                getOptionLabel={(option: any) => option.name}
                onChange={(_event, value) => handleOrgSelect(value)}
                renderOption={(renderProps, option: any) => {
                  return (
                    <li {...renderProps}>
                      <Box>
                        {option.name}
                      </Box>
                    </li>
                  );
                }}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    variant="standard"
                    label={LABELS.ORGANIZATION}
                    placeholder={LABELS.SELECT_ORGANIZATION}
                  />
                )}
              />
            </Box>
            <CustomTooltip title={MESSAGES.ORGANIZATION_HELPER_TEXT}></CustomTooltip>
          </Grid>
          <Grid item xs={12} sm={12} className={classes.formStyle}>
            <AutoCompleteComponent
              values={apps}
              isMultiple={true}
              label={LABELS.APPLICATIONS}
              subGroupData={applicationsList}
              id="applications-auto-complete"
              onSelectionChange={onApplicationChange}
              data-testid="applications-auto-complete"
            />
            <CustomTooltip title={MESSAGES.APPLICATION_HELPER_TEXT}></CustomTooltip>
          </Grid>
          <Grid item xs={12} sm={12} className={classes.formStyleTransferList}>
            <CustomTransferList
              labelId="modules"
              dataInView={selectedModules}
              onSubmit={handleListOnChange}
              dataAvailable={availableModules}
              leftTitle={LABELS.AVAILABLE_MODULES}
              rightTitle={LABELS.SELECTED_MODULES}
            />
          </Grid>
        </Grid>
      </FormDialog>
    </Fragment>
  );
};

export default EditEntitlementDialog;
